import React from 'react'
import { Box, Typography, Paper } from '@mui/material'
import { useReviewCorrections } from './hooks'
import { capitalize, getSanitizedDisplayValue } from './formatUtils'
import { PagedDisplay } from '../../PagedDisplay'
import type { UnchangedEntryItem } from './types'

export function ReviewUnchanged() {
  const { processResult, applyStatus, unchangedEntries } =
    useReviewCorrections()

  if (!processResult) {
    return null
  }

  if (!processResult.results?.[0]?.result) {
    return (
      <Box border={1} borderColor="grey.300" borderRadius={1} p={2} mt={2}>
        <Typography variant="h6" gutterBottom>
          Review Unchanged
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Process result data not available
        </Typography>
      </Box>
    )
  }

  if (unchangedEntries.length === 0) {
    return (
      <Box border={1} borderColor="grey.300" borderRadius={1} p={2} mt={2}>
        <Typography variant="h6" gutterBottom>
          Review Unchanged
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No unchanged entries to review
        </Typography>
      </Box>
    )
  }

  const renderUnchangedItem = (entryItem: UnchangedEntryItem) => (
    <Box key={entryItem.key}>
      <Box
        display="flex"
        alignItems="flex-start"
        p={1}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          '&:last-child': { borderBottom: 'none' },
        }}
      >
        <Box flex={1}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            {capitalize(entryItem.fieldType)} -{' '}
            {entryItem.entry.node_ids.length} node
            {entryItem.entry.node_ids.length !== 1 ? 's' : ''} (no changes
            needed)
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 0.5,
              backgroundColor: '#f5f5f5', // light gray
              color: 'text.primary',
              fontSize: '12px',
              fontFamily: 'Arial, sans-serif',
              wordBreak: 'break-word',
              '& *': {
                margin: 0,
                padding: 0,
                fontFamily: 'Arial, sans-serif',
                fontSize: '12px !important',
                lineHeight: '1.2',
              },
              '& .media-placeholder': {
                display: 'inline-block',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                padding: '2px 4px',
                borderRadius: '2px',
                fontStyle: 'italic',
                color: 'text.secondary',
                fontSize: '11px',
              },
            }}
            dangerouslySetInnerHTML={{
              __html: getSanitizedDisplayValue(
                entryItem.entry.orig_value,
                entryItem.fieldType,
              ),
            }}
          />
        </Box>
      </Box>
    </Box>
  )

  return (
    <Box
      border={1}
      borderColor="grey.300"
      borderRadius={1}
      p={2}
      mt={2}
      sx={{
        opacity: applyStatus ? 0.5 : 1,
        pointerEvents: applyStatus ? 'none' : 'auto',
      }}
    >
      <PagedDisplay
        items={unchangedEntries}
        title="Review Unchanged"
        renderItem={renderUnchangedItem}
        maxHeight={300}
        filterOptions={[
          { label: 'Title', value: 'title' },
          { label: 'Description', value: 'description' },
          { label: 'Body', value: 'body' },
        ]}
        getFilterValue={(item) => item.fieldType}
        filterLabel="Field Type"
      />
    </Box>
  )
}
